import { hasComposition } from '@arpeggi/shared'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { IArpMedia } from '../../../../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../../../../features/arp-registry/ArpRegistry'
import { styled } from '../../../../lib/stitches'
import { useTreeState } from '../../../../stores/Root'
import Button from '../../../core/Button/Button'
import CenteredLoadingAnimation from '../../../core/CenteredLoadingAnimation'
import { IconButton } from '../../../core/Components'
import FavoriteButton from '../../../core/FavoriteButton'
import { DotsVertical, PauseIcon, PlayIcon, PlusIcon } from '../../../core/icons'
import { MediaTypes, MediaTypesType, segmentTrackRemix } from '../../../core/Utils'
import { gray15 } from '../../../platform/color'
import NewStemVisualizer from '../../../studio/discover-stems/NewStemVisualizer'
import ProfileAvatar from './ProfileAvatar'
import Table from './Table'
import { Animated, Spinner, TableProps, useRemix } from './utils'

interface StemRowProps {
  stem: IArpMedia
  active?: boolean
  onClick?: () => void
  hideRowPlayControls?: boolean
}

const StyledVisualizerContainer = styled('div', {
  width: '100%',
  height: '36px',
  display: 'flex',
  opacity: 0.3,
  position: 'relative',
  overflow: 'hidden'
})

const DPI_SCALE = devicePixelRatio || 2
const VISUALIZER_OPTIONS = {
  color: '#000000',
  segmentWidth: DPI_SCALE,
  segmentSpacing: DPI_SCALE * 2
}

const FAKE_URI = `https://d2hgqm22ttdu22.cloudfront.net/cc0-samples-wav2/013_Sabriel_Vocal_Pack/Vox/Sab_NoOnesHomie2.wav`

const VisualizerContainer = StyledVisualizerContainer

const segmentTrackButton = async (index: number, button: string) => {
  let location = window.location.pathname.includes('explore')
    ? 'explore table'
    : window.location.pathname.includes('home')
    ? 'home table'
    : window.location.pathname.includes('profile')
    ? 'profile table'
    : 'unknown'
  const stemDetails = await ArpRegistry.getByIdAsync(index)
  window.analytics.track('Button Clicked', {
    button: button,
    token: index,
    source: window.location.pathname,
    stem: stemDetails.metadata.title,
    location: location,
    type: 'stem',
    artistName: stemDetails.user.displayName,
    artistEthAddress: stemDetails.user.walletAddress,
    genres: stemDetails.metadata.genres,
    bpm: stemDetails.metadata.bpm,
    tags: stemDetails.metadata.tags
  })
}

const StyledPlayIcon = styled(PlayIcon, {
  position: 'absolute',
  left: '16px'
})
const StyledPauseIcon = styled(PauseIcon, {
  position: 'absolute',
  left: '16px'
})
const Play = StyledPlayIcon
const Pause = StyledPauseIcon

export const StemRow: FC<StemRowProps> = observer(
  ({ stem, active, onClick, hideRowPlayControls }) => {
    const { player } = useTreeState()
    const artistAvatar = stem?.user?.profilePicture
    const artistName = stem?.user?.displayName
    const [sample, setSample] = useState<IArpMedia>(null)

    useEffect(() => {
      ArpRegistry.getByIdAsync(stem.subComponents[0]).then((arp) => setSample(arp))
    }, [stem])

    const navigate = useNavigate()
    const handleMore = () => navigate('/stem?token=' + stem.id)

    const { remixStem, status } = useRemix()
    const [hover, setHover] = useState<boolean>(false)
    let indicator = null
    if (!hideRowPlayControls) {
      if (active && player.isPlaying) indicator = <Pause />
      if ((active && !player.isPlaying) || (!active && hover)) indicator = <Play />
    }
    return (
      <Table.Row
        active={active}
        onMouseOver={() => setHover(true)}
        onMouseOut={() => setHover(false)}
        onClick={() => {
          onClick()
          segmentTrackButton(stem.id, 'Table Entry')
        }}
      >
        {indicator}
        <ProfileAvatar eth={stem.user.walletAddress} style={{ width: '26px', height: '26px' }} />
        <Table.Col css={{ width: '160px', marginLeft: '14px' }}>
          <Table.Text bold size={'14'} css={{ color: gray15, whiteSpace: 'nowrap' }}>
            <Animated id={'animated'}>{stem.metadata.title}</Animated>
          </Table.Text>
          <Table.Text medium size={'14'} css={{ whiteSpace: 'nowrap' }} link>
            <Animated id={'animated'} css={{ '&:hover': { textDecoration: 'underline' } }}>
              <Link to={`/profile/${stem.user.walletAddress}`}>{artistName}</Link>
            </Animated>
          </Table.Text>
        </Table.Col>

        <Table.Col css={{ flex: 1 }}>
          {
            hasComposition(stem.asInternalArpMedia()) ? (
              <NewStemVisualizer stem={stem} css={{ width: '100%' }} />
            ) : null
            // ( // We believe this code isn't being used anywhere so should be fine to return null
            //   <SampleVisualizer sample={stem} />
            // )
          }
        </Table.Col>

        <Table.Col
          css={{
            width: '100px',
            marginLeft: '32px',
            display: 'none',
            '@tablet': { display: 'flex' }
          }}
        >
          <Table.Text>
            <Animated id={'animated'}>{(sample && sample.metadata.title) || '-'}</Animated>
          </Table.Text>
        </Table.Col>

        <Table.Col
          css={{
            minWidth: '100px',
            marginLeft: '32px',
            display: 'none',
            '@tablet': { display: 'flex' }
          }}
        >
          <Table.Text>{stem.remixCount}</Table.Text>
        </Table.Col>

        <Table.Col
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'end',
            minWidth: '140px'
          }}
        >
          <Button
            rounded
            type={'outlined'}
            css={{ paddingLeft: '8px', display: 'none', '@tablet': { display: 'flex' } }}
            onClick={(evt) => {
              evt.stopPropagation()
              segmentTrackRemix('stem', stem.id, 'details table')
              remixStem(stem.id)
            }}
          >
            {status === 'loading' ? (
              <Spinner style={{ marginRight: '4px' }} />
            ) : (
              <PlusIcon style={{ marginRight: '4px' }} />
            )}
            {'Studio'}
          </Button>
          <FavoriteButton arpMediaIndex={stem.id} mediaType={MediaTypes.STEM as MediaTypesType} />
          <IconButton
            css={{
              opacity: hover ? 1 : 0.5,
              pointerEvents: !hover && 'none',
              display: 'none',
              '@bigscreen': { display: 'flex' }
            }}
            onClick={handleMore}
          >
            <DotsVertical />
          </IconButton>
        </Table.Col>
      </Table.Row>
    )
  }
)

const StemTable: FC<TableProps> = ({ data, onSelect, selected, loading, hideRowPlayControls }) => {
  return (
    <Table.Root>
      <Table.Header
        css={{
          display: 'none',
          '@tablet': { display: 'flex' }
        }}
      >
        <Table.Col css={{ flex: 1 }}>
          <Table.Text bold>{'Title'}</Table.Text>
        </Table.Col>
        <Table.Col css={{ width: '100px', marginLeft: '32px' }}>
          <Table.Text bold>{'Sample'}</Table.Text>
        </Table.Col>
        <Table.Col css={{ width: '100px', marginLeft: '32px' }}>
          <Table.Text bold>{'Times Used'}</Table.Text>
        </Table.Col>
        <Table.Col css={{ width: '140px' }} />
      </Table.Header>
      <Table.Body>
        {loading ? (
          <CenteredLoadingAnimation />
        ) : (
          data.map((stem: IArpMedia) => (
            <StemRow
              stem={stem}
              key={stem.id}
              onClick={() => onSelect(stem.id)}
              active={selected === stem.id}
              hideRowPlayControls={hideRowPlayControls}
            />
          ))
        )}
      </Table.Body>
    </Table.Root>
  )
}

export default observer(StemTable)
